import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

export class MediaGallery extends Component {
  #controller;
  #eventRoot;
  #config;
  #frame;
  #pendingIndex;
  #deadline = 0;
  #settledFrames = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#config = this.#readConfig(this);
    this.#eventRoot = this.closest('.shopify-section, dialog');

    const { signal } = this.#controller;
    this.#eventRoot?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.#eventRoot?.addEventListener('change', this.#handleColorSelection, { signal });
    this.#eventRoot?.addEventListener('click', this.#handleColorSelection, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    this.#pendingIndex = undefined;
  }

  #readConfig(gallery) {
    const json = gallery.querySelector('[data-color-gallery-config]')?.textContent;
    if (!json) return;

    try {
      const config = JSON.parse(json);
      if (!Array.isArray(config.optionValueIds) || config.optionValueIds.length < 2) return;
      if (!Array.isArray(config.mediaIds) || config.mediaIds.length < config.optionValueIds.length + 1) return;
      return config;
    } catch {
      return;
    }
  }

  #handleColorSelection = (event) => {
    if (!this.#config) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('variant-picker');
    if (!picker || picker.closest('.shopify-section, dialog') !== this.#eventRoot) return;
    if (String(picker.dataset.productId) !== this.#config.productId) return;

    let option;
    if (target instanceof HTMLSelectElement && event.type === 'change') {
      option = target.options[target.selectedIndex];
    } else if (target instanceof HTMLInputElement && target.type === 'radio' && target.checked) {
      option = target;
    } else {
      return;
    }

    const colorIndex = this.#config.optionValueIds.indexOf(option?.dataset.optionValueId);
    if (colorIndex < 0) return;

    this.#pendingIndex = colorIndex + 1;
    this.#deadline = performance.now() + 8000;
    this.#settledFrames = 0;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#moveToPendingSlide);
  };

  #moveToPendingSlide = () => {
    if (!this.isConnected || this.#pendingIndex === undefined) return;
    if (performance.now() > this.#deadline) {
      this.#pendingIndex = undefined;
      return;
    }

    const index = this.#pendingIndex;
    const slideshow = this.slideshow;
    const gridMedia = this.media?.[index];

    if (this.presentation === 'grid' && gridMedia?.getClientRects().length) {
      gridMedia.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'nearest',
      });
      this.#pendingIndex = undefined;
      return;
    }

    if (!slideshow?.slides?.length || slideshow.hasAttribute('transitioning')) {
      this.#settledFrames = 0;
    } else if (++this.#settledFrames >= 2) {
      if (slideshow.current === index) {
        this.#pendingIndex = undefined;
        return;
      }
      if (index >= slideshow.slides.length) {
        this.#pendingIndex = undefined;
        return;
      }
      slideshow.select(index, undefined, { animate: true });
      this.#settledFrames = 0;
    }

    this.#frame = requestAnimationFrame(this.#moveToPendingSlide);
  };

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const nextConfig = this.#readConfig(newMediaGallery);
    if (
      this.#config && nextConfig &&
      this.#config.productId === nextConfig.productId &&
      JSON.stringify(this.#config.optionValueIds) === JSON.stringify(nextConfig.optionValueIds) &&
      JSON.stringify(this.#config.mediaIds) === JSON.stringify(nextConfig.mediaIds)
    ) {
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = (event) => {
    cancelAnimationFrame(this.#frame);
    this.#pendingIndex = undefined;
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
